// Thin tracking layer over gtag.js for Google Ads + GA4.
// - Direct gtag (no GTM dataLayer push).
// - Enhanced Conversions: hashes email/phone client-side with SHA-256
//   per Google's "user-provided data" spec before sending.
// - Safely no-ops when gtag isn't ready or a conversion label is a placeholder.

import { GOOGLE_ADS_ID, GA4_ID, CONVERSION_LABELS, isLabelConfigured } from './analyticsConfig';

const gtagReady = () => typeof window !== 'undefined' && typeof window.gtag === 'function';

/**
 * SHA-256 hash via the browser's Web Crypto API. Returns a hex string.
 * Returns null if input is empty / Web Crypto is unavailable.
 */
async function sha256(value) {
  if (!value || typeof window === 'undefined' || !window.crypto?.subtle) return null;
  const normalised = String(value).trim().toLowerCase();
  if (!normalised) return null;
  const buffer = new TextEncoder().encode(normalised);
  const digest = await window.crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Normalise a phone number to E.164-ish format (digits + leading "+")
 * before hashing — matches Google's Enhanced Conversions guidance.
 */
function normalisePhone(phone) {
  if (!phone) return null;
  const digits = String(phone).replace(/[^\d]/g, '');
  if (!digits) return null;
  return `+${digits}`;
}

/**
 * Build the user_data block for Enhanced Conversions.
 * Send hashed values only. Returns null if nothing usable.
 */
async function buildUserData({ email, phone, firstName, lastName } = {}) {
  const [hashedEmail, hashedPhone, hashedFirstName, hashedLastName] = await Promise.all([
    sha256(email),
    sha256(normalisePhone(phone)),
    sha256(firstName),
    sha256(lastName)
  ]);
  const user_data = {};
  if (hashedEmail) user_data.sha256_email_address = hashedEmail;
  if (hashedPhone) user_data.sha256_phone_number = hashedPhone;
  if (hashedFirstName || hashedLastName) {
    user_data.address = {};
    if (hashedFirstName) user_data.address.sha256_first_name = hashedFirstName;
    if (hashedLastName) user_data.address.sha256_last_name = hashedLastName;
  }
  return Object.keys(user_data).length ? user_data : null;
}

/**
 * Initialise GA4 if a property ID is configured.
 * Google Ads (gtag.js) is already loaded in index.html, so we just
 * register the GA4 config on the existing gtag instance.
 */
export function initGA4() {
  if (!GA4_ID || !gtagReady()) return;
  window.gtag('config', GA4_ID, { send_page_view: true });
}

/**
 * Enable Enhanced Conversions on the Google Ads tag globally.
 * Per Google's docs, this should be called once after gtag is ready.
 */
export function enableEnhancedConversions() {
  if (!gtagReady()) return;
  window.gtag('set', `developer_id.dZmRkOD`, true); // no-op identifier; harmless
  window.gtag('config', GOOGLE_ADS_ID, {
    allow_enhanced_conversions: true
  });
}

/**
 * Fire a Google Ads conversion. Includes hashed user_data when provided
 * (Enhanced Conversions). Safely no-ops on placeholder labels.
 */
async function fireConversion(labelKey, userInfo) {
  if (!gtagReady()) return;
  const label = CONVERSION_LABELS[labelKey];
  if (!isLabelConfigured(label)) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.info(`[analytics] Skipping conversion "${labelKey}" — label placeholder.`);
    }
    return;
  }

  const payload = {
    send_to: `${GOOGLE_ADS_ID}/${label}`
  };

  if (userInfo) {
    const user_data = await buildUserData(userInfo);
    if (user_data) payload.user_data = user_data;
  }

  window.gtag('event', 'conversion', payload);
}

/**
 * Fire a GA4 event (separate from Google Ads conversion).
 */
function fireGA4Event(eventName, params = {}) {
  if (!gtagReady() || !GA4_ID) return;
  window.gtag('event', eventName, { ...params, send_to: GA4_ID });
}

// -------- Public tracking helpers --------

export function trackContactForm(userInfo) {
  fireConversion('contactForm', userInfo);
  fireGA4Event('generate_lead', { method: 'contact_form' });
}

export function trackConsultation(userInfo) {
  fireConversion('consultation', userInfo);
  fireGA4Event('generate_lead', { method: 'consultation_form' });
}

export function trackWhatsAppClick(source = 'unknown') {
  fireConversion('whatsappClick');
  fireGA4Event('click', { link_type: 'whatsapp', source });
}

export function trackCallClick(source = 'unknown') {
  fireConversion('callClick');
  fireGA4Event('click', { link_type: 'phone', source });
}

export function trackNewsletter(userInfo) {
  fireConversion('newsletter', userInfo);
  fireGA4Event('sign_up', { method: 'newsletter' });
}
